import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Container,
    VStack,
    HStack,
    Text,
    Progress,
    Badge,
    Card,
    CardBody,
    Icon,
    Spinner,
    Flex,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Button,
    useToast,
} from '@chakra-ui/react';
import {
    FiCheckCircle,
    FiClock,
    FiAlertCircle,
    FiDatabase,
    FiDownload,
    FiXCircle,
    FiArrowRight,
    FiZap,
} from 'react-icons/fi';
import JobProgressContext from '../context/JobProgressContext';
import ThemeContext from '../context/ThemeContext';

// Animated Number Component
const AnimatedNumber = ({ value, suffix = '', color = 'gray.800', fontSize = '4xl' }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        const duration = 800;
        const startTime = Date.now();
        const startValue = displayValue;
        const endValue = value || 0;

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const current = Math.floor(startValue + (endValue - startValue) * easeOutQuart);
            
            setDisplayValue(current);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
        // eslint-disable-next-line
    }, [value]);

    return (
        <Text fontSize={fontSize} fontWeight="bold" color={color} fontFamily="mono">
            {displayValue.toLocaleString()}{suffix}
        </Text>
    );
};

function LiveJobIdLoader() {
    const { jobStatus } = useContext(JobProgressContext);
    const { themeData } = useContext(ThemeContext);
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    // const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const lightTextColor = isDarkTheme ? '#A0AEC0' : 'gray.400';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const dividerColor = isDarkTheme ? 'rgba(66, 153, 225, 0.4)' : 'gray.300';
    
    // Status colors
    const successColor = isDarkTheme ? '#68D391' : 'green.600';
    const infoColor = isDarkTheme ? accentColor : primaryColor;
    const errorColor = isDarkTheme ? '#FC8181' : 'red.600';
    const processingColor = isDarkTheme ? '#B794F4' : 'purple.600';
    
    // Background colors for different states
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'blue.50';
    const successBg = isDarkTheme ? 'rgba(104, 211, 145, 0.1)' : 'green.50';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.1)' : 'red.50';
    const warningBg = isDarkTheme ? 'rgba(246, 173, 85, 0.1)' : 'orange.50';
    const processingBg = isDarkTheme ? 'rgba(183, 148, 244, 0.1)' : 'purple.50';
    const neutralBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50';
    
    // Progress bar colors
    const progressBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.100';

    // Get job and loading state from context
    const job = jobStatus.job;
    const loading = jobStatus.loading;
    const error = !job && !loading ? 'No job data available' : null;

    const getStatusConfig = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return { color: 'green', icon: FiCheckCircle, label: 'Completed' };
            case 'in_progress':
            case 'processing':
                return { color: 'blue', icon: FiClock, label: 'Processing' };
            case 'failed':
            case 'error':
                return { color: 'red', icon: FiAlertCircle, label: 'Failed' };
            case 'pending':
                return { color: 'orange', icon: FiClock, label: 'Pending' };
            default:
                return { color: 'gray', icon: FiClock, label: status || 'Unknown' };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const calculateDuration = (startedAt, completedAt) => {
        if (!startedAt) return 'N/A';
        const start = new Date(startedAt);
        const end = completedAt ? new Date(completedAt) : new Date();
        const durationMs = end - start;
        const minutes = Math.floor(durationMs / 60000);
        const seconds = Math.floor((durationMs % 60000) / 1000);
        return `${minutes}m ${seconds}s`;
    };

    const handleDownloadErrors = (url, filename) => {
        if (!url) return;
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `errors_${filename}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
            title: 'Downloading errors',
            description: 'Error CSV file download started',
            status: 'info',
            duration: 2000,
            isClosable: true,
        });
    };

    if (loading) {
        return (
            <Box bg={backgroundColor} minH="100vh">
                <Container maxW="container.xl" py={12}>
                    <Flex justify="center" align="center" minH="400px" direction="column" gap={4}>
                        <Spinner 
                            thickness="4px" 
                            speed="0.65s" 
                            emptyColor={progressBg} 
                            color={infoColor} 
                            size="xl" 
                        />
                        <Text color={bodyTextColor} fontSize="md" fontWeight="medium">
                            Loading progress...
                        </Text>
                    </Flex>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box bg={backgroundColor} minH="100vh">
                <Container maxW="container.lg" py={12}>
                    <Alert
                        status="error"
                        variant="left-accent"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        borderRadius="xl"
                        py={10}
                        bg={cardBg}
                        boxShadow="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        <AlertIcon boxSize="40px" mr={0} />
                        <AlertTitle mt={4} mb={2} fontSize="xl" fontWeight="bold" color={strongTextColor}>
                            Failed to Load Progress
                        </AlertTitle>
                        <AlertDescription maxWidth="md" mb={4} color={bodyTextColor}>
                            {error}
                        </AlertDescription>
                    </Alert>
                </Container>
            </Box>
        );
    }

    if (!job) {
        return (
            <Box bg={backgroundColor} minH="100vh">
                <Container maxW="container.lg" py={12}>
                    <Alert
                        status="info"
                        variant="left-accent"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        borderRadius="xl"
                        py={10}
                        bg={cardBg}
                        boxShadow="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        <AlertIcon boxSize="40px" mr={0} />
                        <AlertTitle mt={4} mb={2} fontSize="xl" fontWeight="bold" color={strongTextColor}>
                            No Active Jobs
                        </AlertTitle>
                        <AlertDescription maxWidth="md" color={bodyTextColor}>
                            There are no data processing jobs currently running.
                        </AlertDescription>
                    </Alert>
                </Container>
            </Box>
        );
    }

    const statusConfig = getStatusConfig(job.status);
    const isCompleted = job.status?.toLowerCase() === 'completed';
    const isError = job.status?.toLowerCase() === 'error' || job.status?.toLowerCase() === 'failed';
    const isProcessing = job.status?.toLowerCase() === 'in_progress' || job.status?.toLowerCase() === 'processing';
    const isPending = job.status?.toLowerCase() === 'pending';

    const scoringProgress = job.scoring_progress;
    const hasScoringProgress = scoringProgress && typeof scoringProgress === 'object';
    const scoringProgressPct = hasScoringProgress ? (scoringProgress.progress_pct || 0) : 0;
    const scoringScoredCount = hasScoringProgress ? (scoringProgress.scored_count || 0) : 0;
    const scoringTotalCount = hasScoringProgress ? (scoringProgress.total_count || 0) : 0;
    const scoringFailedCount = hasScoringProgress ? (scoringProgress.failed_count || 0) : 0;
    const scoringStatus = hasScoringProgress ? scoringProgress.status : null;
    const isScoringCompleted = scoringStatus?.toLowerCase() === 'completed';
    const isScoringProcessing = scoringStatus?.toLowerCase() === 'scoring' || scoringStatus?.toLowerCase() === 'in_progress' || scoringStatus?.toLowerCase() === 'processing';

    const isActivelyProcessing = isProcessing || isScoringProcessing;

    const acceptanceRate = job.received_rows > 0 ? ((job.accepted_rows / job.received_rows) * 100).toFixed(1) : 0;

    return (
        <Box bg={backgroundColor} minH="100vh">
            <Container maxW="container.xl" py={8}>
                <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <Box>
                        <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={4}>
                            <Box>
                                <Text fontSize="2xl" fontWeight="bold" color={headingTextColor}>
                                    {job.filename || 'Processing Dataset'}
                                </Text>
                                <Text fontSize="sm" color={mutedTextColor} fontFamily="mono">
                                    Job ID: {job.jobId?.substring(0, 12)}...
                                </Text>
                            </Box>
                            <Badge
                                colorScheme={statusConfig.color}
                                fontSize="md"
                                px={4}
                                py={2}
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                gap={2}
                            >
                                <Icon as={statusConfig.icon} boxSize={4} />
                                {statusConfig.label}
                            </Badge>
                        </HStack>
                    </Box>

                    {/* Pending Warning */}
                    {isPending && (
                        <Alert 
                            status="warning" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            bg={warningBg} 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                    Job Pending
                                </AlertTitle>
                                <AlertDescription fontSize="sm" mt={1} color={bodyTextColor}>
                                    Your job is in the queue and will start processing shortly.
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Data Ingestion Progress */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="xl" 
                        boxShadow="md" 
                        overflow="hidden" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody p={6}>
                            <HStack spacing={2} mb={4}>
                                <Icon as={FiDatabase} boxSize={5} color={infoColor} />
                                <Text fontSize="lg" fontWeight="bold" color={headingTextColor}>
                                    Data Ingestion
                                </Text>
                                {isProcessing && (
                                    <Badge colorScheme="blue" ml={2}>Live</Badge>
                                )}
                                {isCompleted && (
                                    <Badge colorScheme="green" ml={2}>Complete</Badge>
                                )}
                            </HStack>

                            <Flex gap={8} align="center" justify="space-around" flexWrap="wrap" py={6}>
                                <VStack spacing={2}>
                                    <Text fontSize="sm" color={mutedTextColor} fontWeight="medium">
                                        Total Received
                                    </Text>
                                    <AnimatedNumber value={job.received_rows} color={infoColor} />
                                    <Text fontSize="sm" color={lightTextColor}>rows</Text>
                                </VStack>

                                <Icon as={FiArrowRight} boxSize={6} color={dividerColor} />

                                <VStack spacing={2}>
                                    <Text fontSize="sm" color={mutedTextColor} fontWeight="medium">
                                        Accepted
                                    </Text>
                                    <AnimatedNumber value={job.accepted_rows} color={successColor} />
                                    <Text fontSize="sm" color={lightTextColor}>rows</Text>
                                </VStack>

                                {job.rejected_rows > 0 && (
                                    <>
                                        <Icon as={FiArrowRight} boxSize={6} color={dividerColor} />
                                        <VStack spacing={2}>
                                            <Text fontSize="sm" color={mutedTextColor} fontWeight="medium">
                                                Rejected
                                            </Text>
                                            <AnimatedNumber value={job.rejected_rows} color={errorColor} />
                                            <Text fontSize="sm" color={lightTextColor}>rows</Text>
                                        </VStack>
                                    </>
                                )}
                            </Flex>

                            <Box 
                                bg={neutralBg} 
                                p={4} 
                                borderRadius="lg" 
                                mt={4} 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontSize="sm" fontWeight="medium" color={bodyTextColor}>
                                        Acceptance Rate
                                    </Text>
                                    <Text fontSize="lg" fontWeight="bold" color={successColor}>
                                        {acceptanceRate}%
                                    </Text>
                                </Flex>
                                <Progress
                                    value={parseFloat(acceptanceRate)}
                                    size="sm"
                                    borderRadius="full"
                                    colorScheme="green"
                                    bg={progressBg}
                                    sx={{
                                        '& > div': {
                                            backgroundColor: successColor
                                        }
                                    }}
                                />
                            </Box>

                            {isProcessing && (
                                <HStack 
                                    spacing={2} 
                                    mt={4} 
                                    justify="center" 
                                    bg={infoBg} 
                                    py={2} 
                                    px={4} 
                                    borderRadius="full" 
                                    w="fit-content" 
                                    mx="auto"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <Spinner size="sm" color={infoColor} speed="0.8s" />
                                    <Text fontSize="sm" color={bodyTextColor} fontWeight="medium">
                                        Processing rows in real-time...
                                    </Text>
                                </HStack>
                            )}
                        </CardBody>
                    </Card>

                    {/* AI Scoring Progress */}
                    {hasScoringProgress && (
                        <Card 
                            bg={cardBg} 
                            borderRadius="xl" 
                            boxShadow="md" 
                            overflow="hidden" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody p={6}>
                                <HStack spacing={2} mb={4}>
                                    <Icon as={FiZap} boxSize={5} color={processingColor} />
                                    <Text fontSize="lg" fontWeight="bold" color={headingTextColor}>
                                        AI Scoring
                                    </Text>
                                    {isScoringProcessing && (
                                        <Badge colorScheme="purple" ml={2}>In Progress</Badge>
                                    )}
                                    {isScoringCompleted && (
                                        <Badge colorScheme="green" ml={2}>Complete</Badge>
                                    )}
                                </HStack>

                                <Flex justify="space-between" align="baseline" mb={3}>
                                    <Text fontSize="sm" color={bodyTextColor}>
                                        {scoringScoredCount.toLocaleString()} of {scoringTotalCount.toLocaleString()} records
                                    </Text>
                                    <AnimatedNumber value={scoringProgressPct} suffix="%" color={processingColor} fontSize="3xl" />
                                </Flex>

                                <Progress
                                    value={scoringProgressPct}
                                    size="lg"
                                    borderRadius="full"
                                    colorScheme="purple"
                                    hasStripe={isScoringProcessing}
                                    isAnimated={isScoringProcessing}
                                    bg={progressBg}
                                    sx={{
                                        '& > div': {
                                            backgroundColor: processingColor
                                        }
                                    }}
                                />

                                {scoringFailedCount > 0 && (
                                    <HStack spacing={2} mt={3} justify="flex-end">
                                        <Icon as={FiXCircle} boxSize={4} color={errorColor} />
                                        <Text fontSize="sm" color={errorColor} fontWeight="medium">
                                            {scoringFailedCount} failed
                                        </Text>
                                    </HStack>
                                )}

                                {isScoringProcessing && (
                                    <Box 
                                        mt={4} 
                                        bg={processingBg} 
                                        p={4} 
                                        borderRadius="lg" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <Text fontSize="sm" color={bodyTextColor}>
                                            AI models are analyzing each record for customer behavior insights, risk assessment, and engagement patterns.
                                        </Text>
                                    </Box>
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {/* Processing Pipeline */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="xl" 
                        boxShadow="md" 
                        overflow="hidden" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody p={6}>
                            <Text fontSize="lg" fontWeight="bold" color={headingTextColor} mb={6}>
                                Processing Pipeline
                            </Text>
                            
                            <Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
                                <Flex align="center" gap={3} flex="1" minW="200px">
                                    <Box
                                        p={3}
                                        borderRadius="lg"
                                        bg={isProcessing ? infoColor : isCompleted ? successColor : dividerColor}
                                        transition="all 0.3s"
                                    >
                                        <Icon as={FiDatabase} boxSize={6} color="white" />
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="md" fontWeight="semibold" color={strongTextColor}>
                                            Data Ingestion
                                        </Text>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            {isProcessing ? 'In Progress' : isCompleted ? 'Complete' : 'Pending'}
                                        </Text>
                                    </VStack>
                                </Flex>

                                <Icon as={FiArrowRight} boxSize={5} color={lightTextColor} />

                                <Flex align="center" gap={3} flex="1" minW="200px">
                                    <Box
                                        p={3}
                                        borderRadius="lg"
                                        bg={isScoringProcessing ? processingColor : isScoringCompleted ? successColor : dividerColor}
                                        transition="all 0.3s"
                                    >
                                        <Icon as={FiZap} boxSize={6} color="white" />
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="md" fontWeight="semibold" color={strongTextColor}>
                                            AI Scoring
                                        </Text>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            {isScoringProcessing ? 'In Progress' : isScoringCompleted ? 'Complete' : 'Pending'}
                                        </Text>
                                    </VStack>
                                </Flex>

                                <Icon as={FiArrowRight} boxSize={5} color={lightTextColor} />

                                <Flex align="center" gap={3} flex="1" minW="200px">
                                    <Box
                                        p={3}
                                        borderRadius="lg"
                                        bg={isCompleted && isScoringCompleted ? successColor : dividerColor}
                                        transition="all 0.3s"
                                    >
                                        <Icon as={FiCheckCircle} boxSize={6} color="white" />
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="md" fontWeight="semibold" color={strongTextColor}>
                                            Dashboard Ready
                                        </Text>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            {isCompleted && isScoringCompleted ? 'Ready' : 'Waiting'}
                                        </Text>
                                    </VStack>
                                </Flex>
                            </Flex>
                        </CardBody>
                    </Card>

                    {/* Error Message */}
                    {isError && job.error && (
                        <Alert 
                            status="error" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            bg={errorBg} 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle fontSize="md" fontWeight="bold" color={strongTextColor}>
                                    Processing Failed
                                </AlertTitle>
                                <AlertDescription fontSize="sm" mt={1} color={bodyTextColor}>
                                    {job.error}
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Timeline */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="xl" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody p={6}>
                            <Text fontSize="lg" fontWeight="bold" color={headingTextColor} mb={4}>
                                Timeline
                            </Text>
                            <Flex justify="space-between" gap={6} flexWrap="wrap">
                                <Box>
                                    <Text fontSize="xs" color={mutedTextColor} mb={1} fontWeight="semibold" textTransform="uppercase">
                                        Created
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                        {formatDate(job.created_at)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color={mutedTextColor} mb={1} fontWeight="semibold" textTransform="uppercase">
                                        Started
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                        {formatDate(job.started_at)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color={mutedTextColor} mb={1} fontWeight="semibold" textTransform="uppercase">
                                        {isCompleted ? 'Completed' : 'Duration'}
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                        {isCompleted
                                            ? formatDate(job.completed_at)
                                            : calculateDuration(job.started_at, job.completed_at)}
                                    </Text>
                                </Box>
                                {job.data_quality_score !== null && job.data_quality_score !== undefined && (
                                    <Box>
                                        <Text fontSize="xs" color={mutedTextColor} mb={1} fontWeight="semibold" textTransform="uppercase">
                                            Quality Score
                                        </Text>
                                        <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                            {job.data_quality_score}%
                                        </Text>
                                    </Box>
                                )}
                            </Flex>
                        </CardBody>
                    </Card>

                    {/* Error CSV Download */}
                    {job.errors_csv_url && job.rejected_rows > 0 && (
                        <Button
                            leftIcon={<FiDownload />}
                            colorScheme="red"
                            variant="solid"
                            size="lg"
                            onClick={() => handleDownloadErrors(job.errors_csv_url, job.filename)}
                            borderRadius="xl"
                        >
                            Download Error Report ({job.rejected_rows} errors)
                        </Button>
                    )}

                    {/* Status Messages */}
                    {isActivelyProcessing && (
                        <Alert 
                            status="info" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            bg={infoBg} 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                    {isProcessing && isScoringProcessing
                                        ? 'Processing Active'
                                        : isProcessing
                                        ? 'Ingesting Data'
                                        : 'AI Scoring Active'}
                                </AlertTitle>
                                <AlertDescription fontSize="sm" mt={1} color={bodyTextColor}>
                                    {isProcessing && isScoringProcessing
                                        ? 'Data ingestion and AI scoring are running simultaneously. Updates every 3 seconds.'
                                        : isProcessing
                                        ? 'Ingesting your data. AI scoring will begin once complete. Updates every 3 seconds.'
                                        : 'AI models are analyzing your data. Updates every 3 seconds.'}
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {isCompleted && !isScoringProcessing && (
                        <Alert 
                            status="success" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            bg={successBg} 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                    Processing Complete
                                </AlertTitle>
                                <AlertDescription fontSize="sm" mt={1} color={bodyTextColor}>
                                    {hasScoringProgress && isScoringCompleted
                                        ? `All ${scoringScoredCount.toLocaleString()} records analyzed. Your dashboard is ready.`
                                        : 'Your data has been processed successfully. Dashboard is ready.'}
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Auto-refresh indicator */}
                    {isActivelyProcessing && (
                        <Flex justify="center" align="center" gap={2} py={2}>
                            <Spinner size="xs" color={infoColor} speed="0.8s" />
                            <Text fontSize="xs" color={mutedTextColor}>
                                Auto-refreshing every 3 seconds
                            </Text>
                        </Flex>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default LiveJobIdLoader;